import { isRight } from 'fp-ts/Either';
import { createContext, useEffect, useState } from 'react';
import { useAsyncData } from '../hooks/useAsyncData';
import { useAuth, AuthStateType } from '../hooks/useAuth';
import { useTRPC } from '../hooks/useTRPC';
import type { RightResult, RouterOutputs } from '../types/TRPC';
import { captureAndReportError } from '../utils/errorReporting';

type PublisherSummary = RightResult<RouterOutputs['getActivePublisher']>;

interface PublisherContextValue {
  activePublisher: PublisherSummary | null;
  accessiblePublishers: PublisherSummary[];
  isLoading: boolean;
  isSwitching: boolean;
  refresh: () => Promise<void>;
  switchPublisher: (
    publisherUserJoinId: string,
  ) => Promise<PublisherSummary | null>;
}

export const PublisherContext = createContext<PublisherContextValue>({
  activePublisher: null,
  accessiblePublishers: [],
  isLoading: false,
  isSwitching: false,
  refresh: async () => undefined,
  switchPublisher: async () => null,
});

interface Props {
  children?: React.ReactNode;
}

export function PublisherProvider(props: Props) {
  const [authState] = useAuth();
  const trpc = useTRPC();
  const {
    data: activePublisher,
    setData: setActivePublisher,
    isLoading,
    setIsLoading,
    invalidate,
    run,
  } = useAsyncData<PublisherSummary | null>({
    initialData: null,
    initialLoading: true,
  });
  const [accessiblePublishers, setAccessiblePublishers] = useState<
    PublisherSummary[]
  >([]);
  const [isSwitching, setIsSwitching] = useState(false);

  const refresh = async () => {
    if (authState.type !== AuthStateType.Authenticated) {
      invalidate();
      setActivePublisher(null);
      setAccessiblePublishers([]);
      setIsLoading(false);
      return;
    }

    await run(
      () =>
        Promise.all([
          trpc.listAccessiblePublishers.query(undefined).catch(() => null),
          trpc.getActivePublisher.query(undefined).catch(() => null),
        ]),
      {
        onSuccess: ([publishersResult, activeResult], helpers) => {
          if (
            publishersResult &&
            '_tag' in publishersResult &&
            isRight(publishersResult)
          ) {
            setAccessiblePublishers(publishersResult.right);
          } else {
            setAccessiblePublishers([]);
          }

          if (activeResult && '_tag' in activeResult && isRight(activeResult)) {
            helpers.setData(activeResult.right);
          } else {
            helpers.setData(null);
          }
        },
        onError: (error, helpers) => {
          captureAndReportError('Failed to load publisher context:', error);
          helpers.setData(null);
          setAccessiblePublishers([]);
        },
      },
    );
  };

  useEffect(() => {
    void refresh();
  }, [
    authState.type,
    authState.type === AuthStateType.Authenticated
      ? authState.user.onboardingState
      : null,
  ]);

  const switchPublisher = async (publisherUserJoinId: string) => {
    if (authState.type !== AuthStateType.Authenticated) {
      return null;
    }

    setIsSwitching(true);

    try {
      const result = await trpc.setActivePublisher.mutate({
        publisherUserJoinId,
      });

      if (!('_tag' in result) || !isRight(result)) {
        return null;
      }

      setActivePublisher(result.right);

      const publishersResult =
        await trpc.listAccessiblePublishers.query(undefined);
      if (isRight(publishersResult)) {
        setAccessiblePublishers(publishersResult.right);
      }

      return result.right;
    } catch (error) {
      captureAndReportError('Failed to switch publisher:', error);
      return null;
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <PublisherContext.Provider
      value={{
        activePublisher,
        accessiblePublishers,
        isLoading,
        isSwitching,
        refresh,
        switchPublisher,
      }}
    >
      {props.children}
    </PublisherContext.Provider>
  );
}
